import { DateFormatter } from '@repo/toolkit/date'
import { isValid, parseISO } from 'date-fns'
import { z } from 'zod'

import { t } from '../../../locale/utils'

type CreateDateSchemaInput = {
  inputName: string
  maxDate?: Date
  minDate?: Date
  errorMessage?: string
}

export const createDateSchema = (input: CreateDateSchemaInput) => {
  const { inputName, maxDate, minDate, errorMessage } = input

  const formattedMinDate = minDate ? DateFormatter.DDMMYYYY(minDate) : undefined
  const formattedMaxDate = maxDate ? DateFormatter.DDMMYYYY(maxDate) : undefined

  const schema = z
    .date({
      required_error: t('errors.date.required', { inputName }),
      invalid_type_error: t('errors.date.invalid', { inputName }),
      message: t('errors.date.required', { inputName })
    })
    .or(z.coerce.date())
    .refine(
      (date) => {
        if (maxDate && date > maxDate) return false
        if (minDate && date < minDate) return false
        return true
      },
      {
        message:
          errorMessage ||
          (maxDate && minDate
            ? t('errors.date.range', {
                inputName,
                minDate: formattedMinDate,
                maxDate: formattedMaxDate
              })
            : maxDate
              ? t('errors.date.max', { inputName, maxDate: formattedMaxDate })
              : minDate
                ? t('errors.date.min', { inputName, minDate: formattedMinDate })
                : t('errors.date.invalid', { inputName }))
      }
    )

  return schema
}

type CreateDateStringSchemaInput = {
  inputName: string
  minDate?: string
  maxDate?: string
  errorMessage?: string
}

export const createDateStringSchema = (input: CreateDateStringSchemaInput) => {
  const { inputName, maxDate, minDate, errorMessage } = input
  const minDateParsed = minDate ? parseISO(minDate) : undefined
  const maxDateParsed = maxDate ? parseISO(maxDate) : undefined
  const schema = z
    .string({
      required_error: t('errors.date.required', { inputName }),
      invalid_type_error: t('errors.date.invalid', { inputName })
    })
    .refine((val) => val.trim() !== '', {
      message: t('errors.date.required', { inputName })
    })
    .refine(
      (val) => {
        const parsed = parseISO(val)
        return isValid(parsed)
      },
      {
        message: t('errors.date.invalid', { inputName })
      }
    )
    .refine(
      (val) => {
        const parsed = parseISO(val)

        if (!isValid(parsed)) return false
        if (minDateParsed && parsed < minDateParsed) return false
        if (maxDateParsed && parsed > maxDateParsed) return false
        return true
      },
      {
        message:
          errorMessage ||
          (maxDate && minDate
            ? t('errors.date.range', { inputName, minDate, maxDate })
            : maxDate
              ? t('errors.date.max', { inputName, maxDate })
              : minDate
                ? t('errors.date.min', { inputName, minDate })
                : t('errors.date.invalid', { inputName }))
      }
    )

  return schema
}

export const createTimeSchema = (input: CreateDateSchemaInput) => {
  const { inputName } = input
  return z
    .object(
      { hours: z.number(), minutes: z.number() },
      { message: t('errors.time.invalid', { fieldName: inputName }) }
    )
    .refine(
      (time) => {
        const { hours, minutes } = time
        return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60
      },
      { message: t('errors.time.invalid', { fieldName: inputName }) }
    )
}
